/**
 * Editor use cases: map editor for modifying waypoints, blocked cells, and wave params.
 *
 * Bundle-factory exception: all methods share a single mutable `state` closure
 * that cannot be split without duplicating/externalising the state. Per ADR,
 * a single factory returning an explicit bundle is the correct pattern here.
 * Naming: createEditorUseCases(deps) → EditorUseCase bundle.
 *
 * @deprecated alias `createEditor` preserved for backwards compat in composition.ts.
 */

import type { Cell, FieldId } from "../../domain/types.js";
import type { FieldConfigPort, RenderPort, GameStateView } from "../ports.js";
import type { FieldOverrideStorage } from "../../adapters/storage/field-override-adapter.js";

export interface EditorState {
  fieldId: string;
  rows: number;
  cols: number;
  waypoints: Cell[];
  blockedCells: Cell[];
  wavesNum: number;
  monstersPerWave: number;
  monsterBaseHp: number;
  mode: "waypoint" | "block";
  hoverCell: Cell | null;
}

export interface EditorDeps {
  render: RenderPort;
  fieldConfig: FieldConfigPort;
  overrideStorage: FieldOverrideStorage;
  setScene(sceneId: string): void;
  getMapStateView(): GameStateView;
}

export interface EditorUseCase {
  openEditor(fieldId: FieldId): void;
  closeEditor(save: boolean): void;
  editorAddWaypoint(cell: Cell): void;
  editorRemoveLastWaypoint(): void;
  editorClearWaypoints(): void;
  editorToggleBlocked(cell: Cell): void;
  editorSetMode(mode: "waypoint" | "block"): void;
  editorSetWavesNum(n: number): void;
  editorSetMonstersPerWave(n: number): void;
  editorSetMonsterBaseHp(n: number): void;
  editorSetHoverCell(cell: Cell | null): void;
  getEditorState(): EditorState | null;
  isEditorOpen(): boolean;
}

function cellKey(cell: Cell): string {
  return `${cell.row},${cell.col}`;
}

function cellsEqual(a: Cell, b: Cell): boolean {
  return a.row === b.row && a.col === b.col;
}

/** Canonical name: bundle-factory (all methods share mutable editor state). */
export function createEditorUseCases(deps: EditorDeps): EditorUseCase {
  let state: EditorState | null = null;

  function buildView(): GameStateView {
    if (!state) return deps.getMapStateView();
    return {
      scene: "editor" as const,
      editor: {
        fieldId: state.fieldId,
        rows: state.rows,
        cols: state.cols,
        waypoints: [...state.waypoints],
        blockedCells: [...state.blockedCells],
        wavesNum: state.wavesNum,
        monstersPerWave: state.monstersPerWave,
        monsterBaseHp: state.monsterBaseHp,
        mode: state.mode,
        hoverCell: state.hoverCell,
      },
    };
  }

  function renderUpdate(): void {
    deps.render.update(buildView());
  }

  return {
    openEditor(fieldId: FieldId): void {
      const config = deps.fieldConfig.getFieldConfig(fieldId);
      if (!config) return;

      // Load override or seed from config
      const override = deps.overrideStorage.getOverride(fieldId);
      // YAML-authored fields expose entryCells/exitCell instead of the legacy
      // waypoints list — fall back to [entry, exit] as the initial path spine.
      const configWaypoints: Cell[] = config.waypoints && config.waypoints.length > 0
        ? config.waypoints
        : config.entryCells && config.entryCells.length > 0 && config.exitCell
          ? [config.entryCells[0], config.exitCell]
          : [];
      state = {
        fieldId,
        rows: config.rows,
        cols: config.cols,
        waypoints: override?.waypoints && override.waypoints.length >= 2
          ? [...override.waypoints]
          : [...configWaypoints],
        blockedCells: override?.blockedCells
          ? [...override.blockedCells]
          : [...(config.blockedCells ?? [])],
        wavesNum: override?.wavesNum ?? config.wavesNum,
        monstersPerWave: override?.monstersPerWave ?? config.monstersPerWave,
        monsterBaseHp: override?.monsterBaseHp ?? config.monsterBaseHp,
        mode: "waypoint",
        hoverCell: null,
      };

      deps.setScene("editor");
      renderUpdate();
    },

    closeEditor(save: boolean): void {
      if (!state) return;
      const fieldId = state.fieldId;

      if (save) {
        deps.overrideStorage.setOverride(fieldId, {
          waypoints: [...state.waypoints],
          blockedCells: [...state.blockedCells],
          wavesNum: state.wavesNum,
          monstersPerWave: state.monstersPerWave,
          monsterBaseHp: state.monsterBaseHp,
        });
        deps.fieldConfig.invalidate?.(fieldId);
      }

      state = null;
      deps.setScene("map");
      deps.render.setScene("map");
      deps.render.update(deps.getMapStateView());
    },

    editorAddWaypoint(cell: Cell): void {
      if (!state) return;
      // Don't add duplicate consecutive waypoints
      const last = state.waypoints[state.waypoints.length - 1];
      if (last && cellsEqual(last, cell)) return;
      state.waypoints = [...state.waypoints, { ...cell }];
      renderUpdate();
    },

    editorRemoveLastWaypoint(): void {
      if (!state || state.waypoints.length === 0) return;
      state.waypoints = state.waypoints.slice(0, -1);
      renderUpdate();
    },

    editorClearWaypoints(): void {
      if (!state) return;
      state.waypoints = [];
      renderUpdate();
    },

    editorToggleBlocked(cell: Cell): void {
      if (!state) return;
      // Waypoint cells cannot be blocked
      if (state.waypoints.some((wp) => cellsEqual(wp, cell))) return;

      const key = cellKey(cell);
      const existingIndex = state.blockedCells.findIndex((c) => cellKey(c) === key);
      if (existingIndex >= 0) {
        state.blockedCells = state.blockedCells.filter((_, i) => i !== existingIndex);
      } else {
        state.blockedCells = [...state.blockedCells, { ...cell }];
      }
      renderUpdate();
    },

    editorSetMode(mode: "waypoint" | "block"): void {
      if (!state) return;
      state.mode = mode;
      renderUpdate();
    },

    editorSetWavesNum(n: number): void {
      if (!state) return;
      state.wavesNum = Math.max(1, Math.min(80, Math.round(n)));
      renderUpdate();
    },

    editorSetMonstersPerWave(n: number): void {
      if (!state) return;
      state.monstersPerWave = Math.max(1, Math.min(60, Math.round(n)));
      renderUpdate();
    },

    editorSetMonsterBaseHp(n: number): void {
      if (!state) return;
      state.monsterBaseHp = Math.max(1, Math.min(500, Math.round(n)));
      renderUpdate();
    },

    editorSetHoverCell(cell: Cell | null): void {
      if (!state) return;
      state.hoverCell = cell ? { ...cell } : null;
      renderUpdate();
    },

    getEditorState(): EditorState | null {
      return state;
    },

    isEditorOpen(): boolean {
      return state !== null;
    },
  };
}

/** @deprecated Use createEditorUseCases instead. */
export const createEditor = createEditorUseCases;
